import MoreVertIcon from "@mui/icons-material/MoreVert";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { Trans, useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router-dom";
import routing from "routing";
import Menu from "src/components/Default/LekUI/Menu";
import { LongText } from "src/components/Default/LongText";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import ChartIcon from "src/images/Icons/newIcons/chart.svg";
import ResumeExamIcon from "src/images/Icons/newIcons/resume.svg";
import SurveyIcon from "src/images/Icons/newIcons/settings/survey.svg";

interface SectionProps {
	section: any;
	courseId: number;
	activeSectionId: number;
}

const Section = ({ section, courseId, activeSectionId }: SectionProps) => {
	const { t } = useTranslation();
	const { openModal } = useModals();
	const isSomeResolved = section.lessons.some((lesson: any) =>
		["resolved", "incomplete"].includes(lesson.status),
	);
	const examsIds = section.lessons
		.map((lesson: any) => lesson.itemId)
		.filter(Boolean);

	const items = [
		isSomeResolved && {
			Icon: <ResumeExamIcon className="icon-small" />,
			title: t("menuActions.section.resolveAgain"),
			onClick: () =>
				openModal(ModalsNames.RegenerateSectionConfirmationModal, {
					sectionId: section.id,
					courseId,
				}),
		},
		!!examsIds.length && {
			title: t("menuActions.courseSection.statistics"),
			onClick: () => {
				openModal(ModalsNames.ExamsStatisticsModal, {
					examsIds,
				});
			},
			Icon: <ChartIcon className="icon-small" />,
		},
		section.templateSectionId && {
			Icon: <SurveyIcon className="icon-small" />,
			title: t("menuActions.section.reportImprovement"),
			onClick: () =>
				openModal(ModalsNames.ReportCourseReservationsModal, {
					section,
				}),
		},
	].filter(Boolean);

	return (
		<ListItem
			className="section-item"
			data-test-id="course-section-item"
			data-section-id={section.id}
			secondaryAction={
				!!items.length && (
					<Menu icon={<MoreVertIcon />} closeAfterClick items={items} />
				)
			}
			disablePadding
		>
			<ListItemButton
				selected={activeSectionId === section.id}
				className="articles--card--item"
				component={Link}
				to={generatePath(routing.paths.course, {
					id: courseId,
					sectionId: section.id,
				})}
			>
				<ListItemText
					secondary={
						<div className="text-black">
							<Trans
								i18nKey="menuActions.section.remainingLessons"
								values={{
									left: section.statistics.left,
									all: section.statistics.all,
								}}
								components={[<b />]}
							/>
						</div>
					}
					primary={
						<div className="flex items-center justify-between pr-8 gap-8">
							<LongText className="size-16-light flex-1 first-letter-upper">
								{section.name}
							</LongText>
						</div>
					}
				/>
			</ListItemButton>
		</ListItem>
	);
};

interface SectionsProps {
	sections: any[];
	courseId: number;
	activeSectionId: number;
}

export const Sections = ({
	sections,
	courseId,
	activeSectionId,
}: SectionsProps) => {
	return (
		<List className="gap-8 flex flex-col">
			{sections.map((section: any) => (
				<Section
					key={section.id}
					section={section}
					courseId={courseId}
					activeSectionId={activeSectionId}
				/>
			))}
		</List>
	);
};
